// ────────────────────────────────────────────────────────────────────
// voiceCall — roadmap D3: 1:1 P2P WebRTC voice, paired with live co-markup
// so two people who are already looking at the same photo can talk while
// circling corrosion on it (a remote walkdown). Signaling rides the SAME
// WebSocket/room as markupLive.ts (see that file's and
// server/routers/markup_ws.py's module docstrings for why: the two call
// participants are, by construction, the same two people already
// authenticated into this file's markup room).
//
// SCOPE, on purpose: 1:1 only, audio only, no video, no screenshare, no
// recording, no persistence. Multi-party is explicitly CUT by the roadmap
// (docs/superpowers/specs/2026-07-23-workbench-roadmap.md §D3) — a media
// server (SFU) is "another 3am pager plus bandwidth cost", not a v1 gap.
// Recording is a separate, deliberately-deferred product decision: a
// recorded consultation becomes a discoverable record with its own PDPA
// retention/consent obligations.
//
// ── HONESTY NOTE: this call will NOT always connect ──────────────────────
// `ICE_SERVERS` below is public Google STUN ONLY — there is no TURN server
// anywhere in this codebase (a TURN server is separate, non-free infra: it
// relays media byte-for-byte when a direct P2P path can't be found, and
// costs real bandwidth to run). STUN is enough for most home/office
// networks to find each other directly, but it CANNOT punch through
// symmetric NAT or a restrictive corporate firewall — an estimated
// ~10-20% of real-world networks. For those calls, ICE gathering completes,
// an offer/answer exchange completes, and the connection still never comes
// up: `RTCPeerConnection.iceConnectionState` lands on "failed". This hook
// treats that as a distinct, honestly-labeled `ice-failed` state (see
// CallStatus below) rather than leaving the caller staring at a spinner
// forever — but it cannot make the call connect. Fixing that requires
// standing up a TURN server, which is out of scope for this PR.
// ────────────────────────────────────────────────────────────────────
import { useCallback, useEffect, useRef, useState } from "react";
import type { MarkupLiveHandle, RosterPeer } from "./markupLive";

const ICE_SERVERS: RTCIceServer[] = [{ urls: "stun:stun.l.google.com:19302" }];

export type CallStatus =
  | "idle"               // no call in progress
  | "requesting-mic"     // getUserMedia in flight (either starting or accepting)
  | "mic-denied"         // browser/user refused microphone access
  | "ringing-outgoing"   // we sent call_offer, waiting for call_answer
  | "ringing-incoming"   // we received call_offer, waiting on the user to accept/reject
  | "connecting"         // answer exchanged, ICE not yet reporting connected
  | "connected"          // RTCPeerConnection iceConnectionState is connected/completed
  | "ice-failed"         // ICE exhausted candidates without connecting — see honesty note above
  | "busy"               // the room's one call slot was already taken (server call_busy)
  | "ended";              // call ended (either side hung up, or the peer disconnected)

interface PendingOffer {
  peer: RosterPeer;
  sdp: RTCSessionDescriptionInit;
}

export interface VoiceCallHandlers {
  onCallOffer: (peer: RosterPeer, sdp: RTCSessionDescriptionInit) => void;
  onCallAnswer: (peer: RosterPeer, sdp: RTCSessionDescriptionInit) => void;
  onCallIce: (peer: RosterPeer, candidate: RTCIceCandidateInit) => void;
  onCallEnd: (peer: RosterPeer) => void;
  onCallBusy: () => void;
}

export interface VoiceCallApi {
  status: CallStatus;
  /** Name of the other party, once known (set from the offer's or answer's
   *  sender identity — there is no "dial a specific person" step, the call
   *  is offered to whoever else is in the room). Null before either side
   *  has spoken. */
  peerName: string | null;
  muted: boolean;
  remoteStream: MediaStream | null;
  startCall: () => void;
  acceptCall: () => void;
  rejectCall: () => void;
  hangUp: () => void;
  toggleMute: () => void;
  /** Wire these directly into the SAME `connectMarkupLive(fileId, handlers)`
   *  call the markup editor already makes (see PhotoMarkup.tsx) — that is
   *  the "same room" design choice this feature is built around, not a
   *  second connection. Every function here has a STABLE identity for the
   *  life of the hook (empty-deps `useCallback`, state read via refs) so
   *  wiring them in once, at the markup connection's own mount, is safe:
   *  they never go stale even though the underlying call state changes
   *  constantly while the connectMarkupLive effect itself does not rerun. */
  handlers: VoiceCallHandlers;
}

function stopStreamTracks(stream: MediaStream | null): void {
  stream?.getTracks().forEach((t) => t.stop());
}

/** Drains `queueRef` into `pc.addIceCandidate`, for ICE candidates that
 *  arrived over the signaling channel before the local RTCPeerConnection
 *  had a remote description to attach them to yet — a well-known WebRTC
 *  ordering hazard (candidates and the offer/answer race independently over
 *  the same relay). Each candidate is best-effort: one bad/stale candidate
 *  must never abort the rest of the queue or the call. */
function flushIceQueue(pc: RTCPeerConnection, queueRef: { current: RTCIceCandidateInit[] }): void {
  const queued = queueRef.current;
  queueRef.current = [];
  for (const candidate of queued) {
    pc.addIceCandidate(candidate).catch((err: unknown) => {
      console.warn("voiceCall: queued addIceCandidate failed", err);
    });
  }
}

/** 1:1 WebRTC voice call over an already-connected markup live channel.
 *  `live` is the handle returned by `connectMarkupLive` (or the shared noop
 *  handle before it connects — every send method degrades to a no-op the
 *  same way markupLive.ts's own handle does, so this hook never needs its
 *  own "is the socket up" branch).
 *
 *  One call at a time, audio only — see module docstring for the full scope
 *  cut and the STUN-only / no-TURN honesty note that governs whether a
 *  given real call actually connects. */
export function useVoiceCall(_fileId: number, live: MarkupLiveHandle): VoiceCallApi {
  const [status, setStatus] = useState<CallStatus>("idle");
  const [peerName, setPeerName] = useState<string | null>(null);
  const [muted, setMuted] = useState(false);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);

  // Mutable call state lives in refs, not React state — the handlers below
  // must keep a STABLE function identity (see VoiceCallApi.handlers'
  // docstring) so they read "live" values through refs rather than closing
  // over a snapshot from whatever render first wired them in.
  const liveRef = useRef(live);
  const statusRef = useRef<CallStatus>("idle");
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteStreamRef = useRef<MediaStream | null>(null);
  const pendingOfferRef = useRef<PendingOffer | null>(null);
  const iceQueueRef = useRef<RTCIceCandidateInit[]>([]);

  useEffect(() => { liveRef.current = live; }, [live]);
  useEffect(() => { statusRef.current = status; }, [status]);

  const teardown = useCallback((): void => {
    stopStreamTracks(localStreamRef.current);
    localStreamRef.current = null;
    stopStreamTracks(remoteStreamRef.current);
    remoteStreamRef.current = null;
    setRemoteStream(null);
    pcRef.current?.close();
    pcRef.current = null;
    iceQueueRef.current = [];
    pendingOfferRef.current = null;
  }, []);

  const createPeerConnection = useCallback((): RTCPeerConnection => {
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });
    pc.onicecandidate = (ev: RTCPeerConnectionIceEvent) => {
      if (!ev.candidate) return; // null candidate = end-of-gathering marker, nothing to send
      const init = typeof ev.candidate.toJSON === "function"
        ? ev.candidate.toJSON() : (ev.candidate as unknown as RTCIceCandidateInit);
      liveRef.current.sendCallIce(init);
    };
    pc.ontrack = (ev: RTCTrackEvent) => {
      const stream = ev.streams[0] ?? null;
      remoteStreamRef.current = stream;
      setRemoteStream(stream);
    };
    pc.oniceconnectionstatechange = () => {
      const state = pc.iceConnectionState;
      if (state === "connected" || state === "completed") {
        setStatus("connected");
      } else if (state === "failed") {
        // The honest failure state — see module docstring. "disconnected"
        // is deliberately NOT treated as terminal here: it's a common
        // transient blip WebRTC often recovers from on its own, and
        // flipping the UI to a scary error on every brief network hiccup
        // would be less honest, not more.
        setStatus("ice-failed");
      }
    };
    return pc;
  }, []);

  const startCall = useCallback((): void => {
    if (statusRef.current !== "idle" && statusRef.current !== "ended" && statusRef.current !== "busy") return;
    setPeerName(null);
    setStatus("requesting-mic");
    void (async () => {
      let stream: MediaStream;
      try {
        if (!navigator.mediaDevices?.getUserMedia) throw new Error("getUserMedia unsupported");
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (err) {
        console.warn("voiceCall: getUserMedia denied/failed", err);
        setStatus("mic-denied");
        return;
      }
      localStreamRef.current = stream;
      const pc = createPeerConnection();
      pcRef.current = pc;
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      try {
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        liveRef.current.sendCallOffer(offer);
        setStatus("ringing-outgoing");
      } catch (err) {
        console.warn("voiceCall: failed to create/send offer", err);
        teardown();
        setStatus("ended");
      }
    })();
  }, [createPeerConnection, teardown]);

  const acceptCall = useCallback((): void => {
    const pending = pendingOfferRef.current;
    if (!pending) return;
    setStatus("requesting-mic");
    void (async () => {
      let stream: MediaStream;
      try {
        if (!navigator.mediaDevices?.getUserMedia) throw new Error("getUserMedia unsupported");
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (err) {
        console.warn("voiceCall: getUserMedia denied/failed", err);
        setStatus("mic-denied");
        // Tell the caller we can't pick up rather than leaving them ringing
        // forever — call_end is the only "stop ringing" frame this relay
        // understands (see markup_ws.py's CALL_TYPES).
        liveRef.current.sendCallEnd();
        pendingOfferRef.current = null;
        return;
      }
      localStreamRef.current = stream;
      const pc = createPeerConnection();
      pcRef.current = pc;
      stream.getTracks().forEach((t) => pc.addTrack(t, stream));
      try {
        await pc.setRemoteDescription(pending.sdp);
        flushIceQueue(pc, iceQueueRef);
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        liveRef.current.sendCallAnswer(answer);
        setStatus("connecting");
      } catch (err) {
        console.warn("voiceCall: failed to answer call", err);
        teardown();
        setStatus("ended");
      } finally {
        pendingOfferRef.current = null;
      }
    })();
  }, [createPeerConnection, teardown]);

  const rejectCall = useCallback((): void => {
    const pending = pendingOfferRef.current;
    pendingOfferRef.current = null;
    if (pending) liveRef.current.sendCallEnd();
    setStatus("ended");
    setPeerName(null);
  }, []);

  const hangUp = useCallback((): void => {
    const wasActive = statusRef.current !== "idle" && statusRef.current !== "ended";
    teardown();
    if (wasActive) liveRef.current.sendCallEnd();
    setStatus("ended");
    setMuted(false);
    setPeerName(null);
  }, [teardown]);

  const toggleMute = useCallback((): void => {
    const stream = localStreamRef.current;
    if (!stream) return;
    setMuted((prevMuted) => {
      const next = !prevMuted;
      stream.getAudioTracks().forEach((t) => { t.enabled = !next; });
      return next;
    });
  }, []);

  // ── Incoming signaling — see VoiceCallApi.handlers' docstring on why
  // these must stay referentially stable (empty-deps useCallback, refs only).
  const onCallOffer = useCallback((peer: RosterPeer, sdp: RTCSessionDescriptionInit): void => {
    if (statusRef.current !== "idle" && statusRef.current !== "ended" && statusRef.current !== "busy") {
      // Already mid-call (or ringing) — the server's one-slot-per-room rule
      // means a well-behaved second offer shouldn't reach us, but a stray
      // one is ignored rather than interrupting whatever is already
      // happening. No response is sent; the offering peer's UI has no
      // "busy" concept for a peer-to-peer offer that was simply never
      // relayed to it as such — this is a defense-in-depth no-op, not the
      // primary busy path (that lives server-side, see call_busy).
      return;
    }
    pendingOfferRef.current = { peer, sdp };
    iceQueueRef.current = [];
    setPeerName(peer.name);
    setStatus("ringing-incoming");
  }, []);

  const onCallAnswer = useCallback((peer: RosterPeer, sdp: RTCSessionDescriptionInit): void => {
    const pc = pcRef.current;
    if (!pc) return; // an answer with no outstanding offer — nothing to attach it to
    setPeerName(peer.name);
    pc.setRemoteDescription(sdp)
      .then(() => {
        flushIceQueue(pc, iceQueueRef);
        setStatus("connecting");
      })
      .catch((err: unknown) => {
        console.warn("voiceCall: setRemoteDescription(answer) failed", err);
        teardown();
        setStatus("ended");
      });
  }, [teardown]);

  const onCallIce = useCallback((_peer: RosterPeer, candidate: RTCIceCandidateInit): void => {
    const pc = pcRef.current;
    if (pc && pc.remoteDescription) {
      pc.addIceCandidate(candidate).catch((err: unknown) => {
        console.warn("voiceCall: addIceCandidate failed", err);
      });
    } else {
      iceQueueRef.current.push(candidate);
    }
  }, []);

  const onCallEnd = useCallback((_peer: RosterPeer): void => {
    teardown();
    setStatus("ended");
    setMuted(false);
    setPeerName(null);
  }, [teardown]);

  const onCallBusy = useCallback((): void => {
    teardown();
    setStatus("busy");
  }, [teardown]);

  // Unmount (modal close, or navigating away): never leak a live mic or an
  // open RTCPeerConnection just because the user closed the markup editor
  // mid-call. Runs exactly once (mount/unmount only — see empty deps);
  // reads current state through refs, same reasoning as the handlers above.
  useEffect(() => {
    return () => {
      if (statusRef.current !== "idle" && statusRef.current !== "ended") {
        liveRef.current.sendCallEnd();
      }
      teardown();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    status, peerName, muted, remoteStream,
    startCall, acceptCall, rejectCall, hangUp, toggleMute,
    handlers: { onCallOffer, onCallAnswer, onCallIce, onCallEnd, onCallBusy },
  };
}
